import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { CSSProperties } from 'react';
import { FLOATING_VIDEO_Z_INDEX } from '../../libs/overlayZIndex';

export type TConnatixOutstreamConfig = {
  embedScriptUrl: string;
  playerElementId: string;
};

const CONNATIX_RUNTIME_KEY = '__RIO_CONNATIX_OUTSTREAM_RUNTIME';
// The vendor's Strategy Rule serves the embedded experience (with native float-after-in-view)
// when this KVP is 'true'. We keep the player in embedded mode for the whole session and just
// relocate its host between the in-content docked slot and a fixed floating parking box, so the
// player is never torn down or reinitialized as the user navigates. Key name agreed with the
// Connatix/JWP rep; JWP requires lowercase custom parameter names.
const CONNATIX_EMBED_KVP_KEY = 'embedplayer';
const CONNATIX_PLACEMENT_ID_REGEX = /\/placements\/([^/?#]+)/;
const CONNATIX_FLOATING_DESKTOP_WIDTH = 'min(300px, calc(100vw - 10px))';
const CONNATIX_FLOATING_MOBILE_WIDTH = '180px';
const CONNATIX_FLOATING_MOBILE_MEDIA_QUERY = '(max-width: 767px)';
// Sits the floating box above the bottom adhesion ad (bottom_rail is 90px, mobile_footer 100px)
// so the two don't overlap. A plain offset — tune here if the adhesion height changes.
const CONNATIX_FLOATING_BOTTOM = '105px';
// Style for the slot div that ConnatixDockedPlayer renders; the host fills it.
const CONNATIX_DOCKED_BASE_STYLE: CSSProperties = {
  width: '100%',
  height: '100%'
};

const useConnatixRegistrationEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type TConnatixOutstreamProps = {
  config?: TConnatixOutstreamConfig;
  suppressFloating?: boolean;
};

// The player host element and its embed script persist for the whole session so SPA navigation
// never reloads the player. Docked slots adopt the host (a state-preserving move where
// supported); when no slot owns it, it is parked in a fixed floating box.
type TConnatixRuntime = {
  floatingSuppressed: boolean;
  host: HTMLDivElement;
  scriptInjected: boolean;
  ownerSlot: HTMLElement | null;
};

type TJwPlacements = {
  destroy?: () => void;
};

export type TConnatixWindow = Window &
  typeof globalThis & {
    __RIO_CONNATIX_OUTSTREAM_RUNTIME?: TConnatixRuntime;
    jwDataStore?: { custom?: Record<string, Record<string, unknown>> };
    jwplacements?: TJwPlacements;
    RIO_OnProviderBlocked?: () => void;
    RIO_OnProviderLoaded?: () => void;
  };

type TConnatixPlacementContext = {
  dockedPlacementCount: number;
  registerDockedPlacement: () => { unregister: () => void };
};

const ConnatixPlacementContext = createContext<TConnatixPlacementContext>({
  dockedPlacementCount: 0,
  registerDockedPlacement: () => ({
    unregister: () => {
      /* nop */
    }
  })
});

export function buildConnatixPlacementScriptUrl(embedScriptUrl: string, playerElementId: string): string {
  const baseUrl = typeof window === 'undefined' ? 'https://raider.io/' : window.location.href;
  const url = new URL(embedScriptUrl, baseUrl);
  url.searchParams.set('divId', playerElementId);
  return url.toString();
}

export function getConnatixPlacementId(embedScriptUrl: string): string | null {
  return CONNATIX_PLACEMENT_ID_REGEX.exec(embedScriptUrl)?.[1] ?? null;
}

export function setConnatixEmbedKvp(win: TConnatixWindow, embedScriptUrl: string, isEmbedded: boolean): void {
  const placementId = getConnatixPlacementId(embedScriptUrl);
  if (!placementId) {
    console.error('[Connatix] unable to derive placement id from embed script url');
    return;
  }

  const jwDataStore = win.jwDataStore || {};
  const custom = jwDataStore.custom || {};
  custom[placementId] = {
    ...custom[placementId],
    [CONNATIX_EMBED_KVP_KEY]: isEmbedded ? 'true' : 'false'
  };
  jwDataStore.custom = custom;
  win.jwDataStore = jwDataStore;
}

// The host has two placement styles: a fixed bottom-right box on pages with no docked slot (the
// player reads as a floating unit without us fighting the vendor's positioning), and filling the
// docked slot it has been moved into. Both objects list the same keys so switching between them
// always resets the other mode's properties.
const CONNATIX_FLOATING_BASE_STYLE: Partial<CSSStyleDeclaration> = {
  display: '',
  position: 'fixed',
  right: '5px',
  bottom: CONNATIX_FLOATING_BOTTOM,
  height: '',
  aspectRatio: '16 / 9',
  zIndex: String(FLOATING_VIDEO_Z_INDEX),
  overflow: 'hidden'
};
const CONNATIX_DOCKED_HOST_STYLE: Partial<CSSStyleDeclaration> = {
  display: '',
  position: '',
  right: '',
  bottom: '',
  width: '100%',
  height: '100%',
  aspectRatio: '',
  zIndex: '',
  overflow: ''
};

function getConnatixFloatingStyle(suppressed = false): Partial<CSSStyleDeclaration> {
  const isMobile = window.matchMedia?.(CONNATIX_FLOATING_MOBILE_MEDIA_QUERY).matches;

  return {
    ...CONNATIX_FLOATING_BASE_STYLE,
    display: suppressed ? 'none' : '',
    width: isMobile ? CONNATIX_FLOATING_MOBILE_WIDTH : CONNATIX_FLOATING_DESKTOP_WIDTH
  };
}

function applyConnatixHostStyle(host: HTMLDivElement, style: Partial<CSSStyleDeclaration>) {
  Object.assign(host.style, style);
}

function moveConnatixHost(parent: HTMLElement, host: HTMLDivElement) {
  if (host.parentElement === parent) {
    return;
  }

  const parentWithMove = parent as HTMLElement & { moveBefore?: (node: Node, referenceNode: Node | null) => void };
  if (typeof parentWithMove.moveBefore === 'function' && host.isConnected) {
    try {
      // Atomic move keeps the player iframes alive; appendChild would reload them.
      parentWithMove.moveBefore(host, null);
      return;
    } catch {
      // fall through to appendChild for edge cases (e.g. cross-document moves)
    }
  }
  parent.appendChild(host);
}

function parkConnatixHost(runtime: TConnatixRuntime) {
  runtime.ownerSlot = null;
  applyConnatixHostStyle(runtime.host, getConnatixFloatingStyle(runtime.floatingSuppressed));
  moveConnatixHost(document.body, runtime.host);
}

function ensureConnatixRuntime(win: TConnatixWindow, config: TConnatixOutstreamConfig): TConnatixRuntime {
  const existing = win[CONNATIX_RUNTIME_KEY];
  if (existing) {
    return existing;
  }

  const host = document.createElement('div');
  host.id = config.playerElementId;
  host.style.pointerEvents = 'none';
  applyConnatixHostStyle(host, getConnatixFloatingStyle());
  document.body.appendChild(host);

  const runtime: TConnatixRuntime = {
    floatingSuppressed: false,
    host,
    scriptInjected: false,
    ownerSlot: null
  };
  win[CONNATIX_RUNTIME_KEY] = runtime;
  return runtime;
}

function bootstrapConnatixPlacement(win: TConnatixWindow, config: TConnatixOutstreamConfig) {
  const runtime = ensureConnatixRuntime(win, config);
  if (runtime.scriptInjected) {
    return;
  }

  // Single embed mode for the whole session — the vendor serves the embedded experience and we
  // handle placement, so the player is never reinitialized on navigation.
  setConnatixEmbedKvp(win, config.embedScriptUrl, true);

  const script = document.createElement('script');
  script.async = true;
  script.src = buildConnatixPlacementScriptUrl(config.embedScriptUrl, config.playerElementId);
  script.onload = () => {
    if (win[CONNATIX_RUNTIME_KEY] !== runtime) {
      return;
    }

    runtime.host.style.pointerEvents = 'auto';
    win.RIO_OnProviderLoaded?.();
  };
  script.onerror = () => {
    if (win[CONNATIX_RUNTIME_KEY] !== runtime) {
      return;
    }

    win.RIO_OnProviderBlocked?.();
  };
  runtime.host.appendChild(script);
  runtime.scriptInjected = true;
}

export const ConnatixPlacementProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dockedPlacementCount, setDockedPlacementCount] = useState(0);
  const registerDockedPlacement = useCallback(() => {
    let isRegistered = true;
    setDockedPlacementCount((count) => count + 1);

    return {
      unregister: () => {
        if (!isRegistered) {
          return;
        }

        isRegistered = false;
        setDockedPlacementCount((count) => count - 1);
      }
    };
  }, []);
  const value = useMemo(
    () => ({
      dockedPlacementCount,
      registerDockedPlacement
    }),
    [dockedPlacementCount, registerDockedPlacement]
  );

  return <ConnatixPlacementContext.Provider value={value}>{children}</ConnatixPlacementContext.Provider>;
};

export function useConnatixDockedPlacementRegistration(enabled: boolean) {
  const { registerDockedPlacement } = useContext(ConnatixPlacementContext);

  useConnatixRegistrationEffect(() => {
    if (!enabled) {
      return;
    }

    const registration = registerDockedPlacement();

    return () => {
      registration.unregister();
    };
  }, [enabled, registerDockedPlacement]);
}

// Renders the in-page slot for the embedded player and adopts the persistent host into it. On
// unmount it parks the host back into the floating box so the player keeps playing uninterrupted.
export const ConnatixDockedPlayer: FC<TConnatixOutstreamProps> = ({ config }) => {
  const slotRef = useRef<HTMLDivElement | null>(null);

  useConnatixRegistrationEffect(() => {
    const win = window as TConnatixWindow;
    const slot = slotRef.current;
    if (!config?.embedScriptUrl || !config.playerElementId || !slot) {
      console.error('[Connatix] missing placement embed configuration');
      return;
    }

    const runtime = ensureConnatixRuntime(win, config);
    runtime.ownerSlot = slot;
    applyConnatixHostStyle(runtime.host, CONNATIX_DOCKED_HOST_STYLE);
    moveConnatixHost(slot, runtime.host);

    // Pages that render a docked slot without the app chrome (e.g. the ad-testing page) have no
    // controller mounted, so bootstrap the embed script from here.
    bootstrapConnatixPlacement(win, config);

    return () => {
      // Only park if this slot still owns the host — a newer slot may have adopted it already.
      // Parking is synchronous so the host is moved to safety before React removes this slot.
      if (win[CONNATIX_RUNTIME_KEY] !== runtime || runtime.ownerSlot !== slot) {
        return;
      }

      parkConnatixHost(runtime);
    };
  }, [config?.embedScriptUrl, config?.playerElementId]);

  return <div ref={slotRef} style={CONNATIX_DOCKED_BASE_STYLE} />;
};

// Pending teardown timer, deferred so a React StrictMode (dev) unmount→remount cycle can cancel it
// instead of destroying and re-injecting the session-persistent player.
let connatixTeardownTimer = 0;

// Session-wide controller (rendered by AppBase): creates the persistent host and injects the embed
// script once, and tears it down only when the controller genuinely unmounts (e.g. ads turned off).
// Placement is handled by the docked slots adopting the host (ensureConnatixRuntime parks it in the
// floating box on creation, and each slot parks it again on unmount), so no per-navigation work is
// needed here.
export const ConnatixOutstreamController: FC<TConnatixOutstreamProps> = ({ config, suppressFloating = false }) => {
  useEffect(() => {
    if (!config?.embedScriptUrl || !config.playerElementId) {
      console.error('[Connatix] missing placement embed configuration');
      return;
    }

    const win = window as TConnatixWindow;
    // Cancel a pending teardown — React just unmounted/remounted us (StrictMode double-invoke or a
    // transient re-render), so the existing player should survive rather than be re-injected.
    window.clearTimeout(connatixTeardownTimer);

    const runtime = win[CONNATIX_RUNTIME_KEY];
    if (runtime) {
      const wasFloatingSuppressed = runtime.floatingSuppressed;
      runtime.floatingSuppressed = suppressFloating;
      if (!runtime.ownerSlot && (suppressFloating || wasFloatingSuppressed)) {
        parkConnatixHost(runtime);
      }
    }

    // Desktop and consented mobile traffic keep the original synchronous controller bootstrap.
    // An unresolved mobile cookie footer suppresses only the root floating placement; a docked
    // player can still bootstrap itself in ConnatixDockedPlayer.
    if (!suppressFloating) {
      bootstrapConnatixPlacement(win, config);
    }

    return () => {
      // Defer teardown so a StrictMode remount can cancel it; only a genuine unmount runs it.
      connatixTeardownTimer = window.setTimeout(() => {
        const activeRuntime = win[CONNATIX_RUNTIME_KEY];
        if (!activeRuntime) {
          return;
        }

        win.jwplacements?.destroy?.();
        activeRuntime.host.remove();
        delete win[CONNATIX_RUNTIME_KEY];
      }, 0);
    };
  }, [config?.embedScriptUrl, config?.playerElementId, suppressFloating]);

  return null;
};
